import { forwardRef, useMemo } from "react";
import { Radio as AriaRadio } from "react-aria-components";

import { cn } from "../../utils/cn";
import { useRadioContext } from "./context";
import { radioVariants } from "./radioVariants";

import type { RadioProps as AriaRadioProps } from "react-aria-components";

/** Event type matching antd Radio onChange signature for standalone mode */
export interface RadioChangeEvent {
  target: {
    checked: boolean;
  };
}

export interface RadioProps extends Omit<AriaRadioProps, "onChange" | "value"> {
  /** Optional label text shown above the value */
  label?: string;
  /** Optional description text shown below the value */
  description?: string;
  /**
   * Value for RadioGroup mode. Required when used within RadioGroup.
   * Not required in standalone mode (when `checked` prop is provided).
   */
  value?: string;
  /**
   * Controlled checked state for standalone mode (outside RadioGroup).
   * When provided, Radio works as a standalone controlled component.
   */
  checked?: boolean;
  /**
   * Change handler for standalone mode with antd-compatible signature.
   * Called with `{ target: { checked: boolean } }` when the radio is clicked.
   */
  onChange?: (e: RadioChangeEvent) => void;
  /**
   * Disabled state (antd-compatible alias for isDisabled).
   * Either `disabled` or `isDisabled` can be used.
   */
  disabled?: boolean;
}

/**
 * Radio component that works in two modes:
 *
 * **RadioGroup mode** — place inside `<RadioGroup>` with a `value` prop.
 * The group handles mutual exclusion automatically.
 * ```tsx
 * <RadioGroup defaultValue="a">
 *   <RadioGroup.Radio value="a">Option A</RadioGroup.Radio>
 *   <RadioGroup.Radio value="b">Option B</RadioGroup.Radio>
 * </RadioGroup>
 * ```
 *
 * **Standalone mode** — provide `checked` and `onChange` for individual control.
 * This matches antd's Radio API and supports toggle behavior.
 * ```tsx
 * <Radio checked={isOn} onChange={e => setIsOn(e.target.checked)}>Toggle</Radio>
 * ```
 *
 * The mode is detected automatically: if `checked` is provided, standalone mode
 * is used; otherwise the component expects to be inside a RadioGroup.
 */
export const Radio = forwardRef<HTMLLabelElement, RadioProps>(
  (
    {
      className,
      children,
      label,
      description,
      value,
      checked,
      onChange,
      isDisabled,
      disabled,
      ...props
    },
    ref
  ) => {
    const context = useRadioContext();

    // Support both `disabled` (antd) and `isDisabled` (react-aria) props
    const isComponentDisabled = disabled ?? isDisabled;

    // Default slots for standalone mode when no RadioGroup context
    const defaultSlots = useMemo(() => radioVariants(), []);
    const slots = context?.slots ?? defaultSlots;
    const classNames = context?.classNames;

    // Standalone mode: render without react-aria RadioGroup
    if (checked !== undefined) {
      const handleClick = () => {
        if (!isComponentDisabled && onChange) {
          // Toggle behavior: if checked, uncheck; if unchecked, check
          onChange({ target: { checked: !checked } });
        }
      };

      const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          handleClick();
        }
      };

      return (
        <label
          ref={ref}
          className={cn(
            slots.radio({ className: cn(classNames?.radio, className) }),
            isComponentDisabled && "cursor-not-allowed opacity-60"
          )}
          data-selected={checked || undefined}
          data-disabled={isComponentDisabled || undefined}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="radio"
          aria-checked={checked}
          aria-disabled={isComponentDisabled}
          tabIndex={isComponentDisabled ? -1 : 0}
        >
          <div className={cn(slots.radioContent(), classNames?.radioContent)}>
            {/* Optional per-radio label */}
            {label && <div className={cn(slots.radioLabel(), classNames?.radioLabel)}>{label}</div>}

            <div className={cn(slots.radioInner(), classNames?.radioInner)}>
              {/* Radio indicator circle with inner dot */}
              <div
                className={cn(slots.indicator(), classNames?.indicator)}
                data-selected={checked || undefined}
                data-disabled={isComponentDisabled || undefined}
              >
                <div
                  className={cn(slots.dot(), classNames?.dot)}
                  data-selected={checked || undefined}
                  data-disabled={isComponentDisabled || undefined}
                />
              </div>

              {/* Value and optional description */}
              <div className={cn(slots.labelWrapper(), classNames?.labelWrapper)}>
                <div
                  className={cn(slots.label(), classNames?.label)}
                  data-disabled={isComponentDisabled || undefined}
                >
                  {children as React.ReactNode}
                </div>
                {description && (
                  <div
                    className={cn(slots.radioDescription(), classNames?.radioDescription)}
                    data-disabled={isComponentDisabled || undefined}
                  >
                    {description}
                  </div>
                )}
              </div>
            </div>
          </div>
        </label>
      );
    }

    // RadioGroup mode: use react-aria Radio
    return (
      <AriaRadio
        {...props}
        value={value ?? ""}
        ref={ref}
        isDisabled={isComponentDisabled}
        className={slots.radio({ className: cn(classNames?.radio, className) })}
      >
        <div className={cn(slots.radioContent(), classNames?.radioContent)}>
          {/* Optional per-radio label */}
          {label && <div className={cn(slots.radioLabel(), classNames?.radioLabel)}>{label}</div>}

          <div className={cn(slots.radioInner(), classNames?.radioInner)}>
            {/* Radio indicator circle with inner dot */}
            <div className={cn(slots.indicator(), classNames?.indicator)}>
              <div className={cn(slots.dot(), classNames?.dot)} />
            </div>

            {/* Value and optional description */}
            <div className={cn(slots.labelWrapper(), classNames?.labelWrapper)}>
              <div className={cn(slots.label(), classNames?.label)}>
                {children as React.ReactNode}
              </div>
              {description && (
                <div className={cn(slots.radioDescription(), classNames?.radioDescription)}>
                  {description}
                </div>
              )}
            </div>
          </div>
        </div>
      </AriaRadio>
    );
  }
);
Radio.displayName = "Radio";
